import { __awaiter } from "tslib";
import qs from 'qs';
import { parseGetLinguisticControlsResponse } from '@/search-for-retail/linguistic-controls/linguistic-controls.parsers';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path prefix for linguistic control endpoints. */
const LINGUISTIC_CONTROLS_PATH = 'linguistic';
// ---------------------------------------------------------------------------
// Shared tag helpers
// ---------------------------------------------------------------------------
const invalidateLinguisticControlTags = (_result, _error, { id }) => [
    CC_API_TAGS.LinguisticControlList,
    { type: CC_API_TAGS.LinguisticControl, id },
];
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for linguistic controls data.
 *
 * Area-scoped endpoints sit under `/linguistic/{areaId}/controls`.
 * Collection-scoped quota endpoints sit under `/collections/{collection}/quotas/{metric}`.
 * Auth and tenant headers are handled by the base `ccApi`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const linguisticControlsApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Linguistic controls queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the paginated list of linguistic controls for an area.
         *
         * @endpoint GET /linguistic/{areaId}/controls?controlType=&page=&size=&searchTerm=
         * @returns Parsed LinguisticControlModel via transformResponse
         */
        getLinguisticControlsList: builder.query({
            query: ({ areaId, page, perPage, action, filter, }) => {
                const queryParams = qs.stringify({
                    controlType: action,
                    page,
                    size: perPage,
                    searchTerm: filter === null || filter === void 0 ? void 0 : filter.searchTerm,
                });
                return `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls?${queryParams}`;
            },
            transformResponse: (response) => parseGetLinguisticControlsResponse(response),
            providesTags: [CC_API_TAGS.LinguisticControlList],
        }),
        /**
         * Fetches a single linguistic control by ID.
         *
         * @endpoint GET /linguistic/{areaId}/controls/{id}
         */
        getLinguisticControlById: builder.query({
            query: ({ areaId, id }) => `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls/${id}`,
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.LinguisticControl, id },
            ],
        }),
        /**
         * Fetches the controls count breakdown for an area.
         *
         * Used by both the table container (full object) and the form manager (totalCount only).
         *
         * @endpoint GET /linguistic/{areaId}/controls/count
         */
        getLinguisticControlsCount: builder.query({
            query: ({ areaId }) => `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls/count`,
            providesTags: [CC_API_TAGS.LinguisticControlList],
        }),
        /**
         * Fetches the quota configuration for a collection metric.
         *
         * @endpoint GET /collections/{collection}/quotas/{metric}
         */
        getControlsQuota: builder.query({
            query: ({ collection, metric }) => `/collections/${collection}/quotas/${metric}`,
        }),
        /**
         * Fetches the current quota usage for a collection metric.
         *
         * @endpoint GET /collections/{collection}/quotas/{metric}/usage
         */
        getControlsQuotaUsage: builder.query({
            query: ({ collection, metric }) => `/collections/${collection}/quotas/${metric}/usage`,
        }),
        // -----------------------------------------------------------------------
        // Linguistic controls mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new linguistic control in an area.
         *
         * Extracts the `x-groupby-location-page` response header so consumers
         * can highlight newly created models (feature-flag gated).
         *
         * @endpoint POST /linguistic/{areaId}/controls
         */
        createLinguisticControl: builder.mutation({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ areaId, data }, _queryApi, _extraOptions, baseQuery) {
                    var _b, _c, _d;
                    const result = yield baseQuery({
                        url: `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls`,
                        method: 'POST',
                        body: data,
                    });
                    if (result.error) {
                        return { error: result.error };
                    }
                    const control = result.data;
                    const meta = result.meta;
                    const locationPage = (_d = (_c = (_b = meta === null || meta === void 0 ? void 0 : meta.response) === null || _b === void 0 ? void 0 : _b.headers) === null || _c === void 0 ? void 0 : _c.get('x-groupby-location-page')) !== null && _d !== void 0 ? _d : null;
                    return { data: { control, locationPage } };
                });
            },
            invalidatesTags: [CC_API_TAGS.LinguisticControlList],
        }),
        /**
         * Updates an existing linguistic control.
         *
         * Inlines the `controlId` field into the request body, matching the
         * legacy client behaviour (`{ ...data, controlId: id }`).
         *
         * @endpoint PUT /linguistic/{areaId}/controls/{id}
         */
        updateLinguisticControlById: builder.mutation({
            query: ({ areaId, id, data }) => ({
                url: `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls/${id}`,
                method: 'PUT',
                body: Object.assign(Object.assign({}, data), { controlId: id }),
            }),
            invalidatesTags: invalidateLinguisticControlTags,
        }),
        /**
         * Deletes a single linguistic control from an area.
         *
         * @endpoint DELETE /linguistic/{areaId}/controls/{id}
         */
        deleteLinguisticControl: builder.mutation({
            query: ({ areaId, id }) => ({
                url: `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: invalidateLinguisticControlTags,
        }),
        /**
         * Bulk-deletes linguistic controls from an area.
         *
         * Uses `qs` `{ arrayFormat: 'comma' }` serialization for the `controlIds` query param.
         *
         * @endpoint DELETE /linguistic/{areaId}/controls?controlIds={csv}
         */
        bulkDeleteLinguisticControls: builder.mutation({
            query: ({ areaId, controlIds }) => {
                const queryParams = qs.stringify({ controlIds }, { arrayFormat: 'comma' });
                return {
                    url: `/${LINGUISTIC_CONTROLS_PATH}/${areaId}/controls?${queryParams}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [CC_API_TAGS.LinguisticControlList],
        }),
    }),
});
export const { useGetLinguisticControlsListQuery, useLazyGetLinguisticControlsListQuery, useGetLinguisticControlByIdQuery, useLazyGetLinguisticControlByIdQuery, useGetLinguisticControlsCountQuery, useLazyGetLinguisticControlsCountQuery, useGetControlsQuotaQuery, useLazyGetControlsQuotaQuery, useGetControlsQuotaUsageQuery, useLazyGetControlsQuotaUsageQuery, useCreateLinguisticControlMutation, useUpdateLinguisticControlByIdMutation, useDeleteLinguisticControlMutation, useBulkDeleteLinguisticControlsMutation, } = linguisticControlsApi;
